import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { ReconciliationService } from "@/api";
import type { reconciliation_SubsidyChildTotalsResponseDTO } from "@/api";

export const subsidyChildTotalsKeys = {
  all: ["reconciliation", "subsidy-child-totals"] as const,
  detail: (
    orgId: string | null,
    debtorId: string | null,
    period: { start: string; end: string } | null,
    filter: { status?: string; q?: string; sort?: string; match?: string } | null,
  ) =>
    [
      ...subsidyChildTotalsKeys.all,
      "detail",
      orgId,
      debtorId,
      period,
      filter,
    ] as const,
};

export interface UseSubsidyChildTotalsParams {
  debtorId: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  status?: string;
  q?: string;
  sort?: string;
  /** Expected-vs-Cash-in lens (issue #761, Phase 4): "all" | "unmatched". */
  match?: string;
  enabled?: boolean;
}

export function useSubsidyChildTotals(params: UseSubsidyChildTotalsParams) {
  const { orgId } = useAuth();
  const enabled =
    params.enabled !== false &&
    params.debtorId !== null &&
    params.debtorId !== "";
  return useQuery<reconciliation_SubsidyChildTotalsResponseDTO>({
    queryKey: subsidyChildTotalsKeys.detail(
      orgId ?? null,
      params.debtorId,
      params.periodStart && params.periodEnd
        ? { start: params.periodStart, end: params.periodEnd }
        : null,
      { status: params.status, q: params.q, sort: params.sort, match: params.match },
    ),
    queryFn: () =>
      ReconciliationService.getApiV1ReconciliationDebtorAccountsSubsidyLedgerChildTotals(
        params.debtorId as string,
        params.periodStart ?? undefined,
        params.periodEnd ?? undefined,
        (params.status || undefined) as
          | "all"
          | "outstanding"
          | "overpaid"
          | undefined,
        params.q || undefined,
        (params.sort || undefined) as
          | "child_asc"
          | "child_desc"
          | "child_id_asc"
          | "child_id_desc"
          | "expected_asc"
          | "expected_desc"
          | "paid_asc"
          | "paid_desc"
          | "outstanding_asc"
          | "outstanding_desc"
          | undefined,
        (params.match || undefined) as "all" | "unmatched" | undefined,
      ),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });
}
